import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import pluralize from 'pluralize';
import { filter, take } from 'rxjs/operators';
import { FAKE_CHECK_IN_QTY_FIELD, FAKE_QUANTITY_ON_LOAN_FIELD } from '../../../../../shared/fields/fake-fields';
import { STANDARD_FIELDS } from '../../../../../shared/fields/standard-fields';
import * as fromRoot from '../../../core/store';
import * as assetEventActions from '../../../core/store/asset-events/asset-event.actions';
import * as layoutActions from '../../../core/store/layout/layout.actions';
import { Asset, AssetEvent } from '../../../core/store/models';
import { assetEventSelectors } from '../../../core/store/selectors';
import { ActionStatus, SavingMessage, Severity } from '../../../core/store/types';
import * as assetActions from '../../../core/store/assets/asset.actions';

export interface CheckInModalData {
    assetEventsForAssetId: { [assetId: number]: AssetEvent };
    assets: Asset[];
    eventId: number;
}

@Component({
    templateUrl: './check-in-modal.component.html',
    styles: [`:host ::ng-deep .mdc-dialog__title+.mdc-dialog__content { padding: 24px;}
              :host ::ng-deep .mdc-text-field--filled.mdc-text-field--disabled { background-color: unset;}`],
    standalone: false
})
export class CheckInModalComponent implements OnInit {
    assetEventsForAssetId: { [assetId: number]: AssetEvent };
    assets: Asset[];
    checkInQtyField = FAKE_CHECK_IN_QTY_FIELD;
    eventId: number;
    modalForm: UntypedFormGroup[] = [];
    nameField = STANDARD_FIELDS.name;
    quantityOnLoanField = FAKE_QUANTITY_ON_LOAN_FIELD;
    saveDisabled = false;
    savingMessage?: SavingMessage[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) private data: CheckInModalData, public dialogRef: MatDialogRef<CheckInModalComponent>, private fb: UntypedFormBuilder, private store: Store<fromRoot.State>) { }

    ngOnInit() {
        this.assets = this.data.assets;
        this.assetEventsForAssetId = this.data.assetEventsForAssetId;
        this.eventId = this.data.eventId;
        this.buildForm();
    }

    buildForm() {
        this.assets.forEach(asset => {
            const assetEvent = this.assetEventsForAssetId[asset.id];
            const formObj = {};
            formObj[this.nameField.canonical_name] = [{ value: asset.name, disabled: true }];
            formObj[this.quantityOnLoanField.canonical_name] = [{ value: assetEvent.qty_on_loan, disabled: true }];
            formObj[this.checkInQtyField.canonical_name] = [
                { value: assetEvent.qty_on_loan, disabled: false },
                [Validators.min(0), Validators.max(assetEvent.qty_on_loan)]
            ];
            this.modalForm[asset.id] = this.fb.group(formObj);
        });
    }

    // Checks that the check in quantity is not negative or greater than the quantity on loan
    validateCheckInQtyValues() {
        return this.assets.every(asset => {
            const values = this.modalForm[asset.id].getRawValue();
            const qtyOnLoan = this.assetEventsForAssetId[asset.id] ? this.assetEventsForAssetId[asset.id].qty_on_loan : 0;
            const checkInQty = values[this.checkInQtyField.canonical_name] ? Number(values[this.checkInQtyField.canonical_name]) : 0;
            if (checkInQty < 0 || checkInQty > qtyOnLoan || !Number.isInteger(checkInQty)) {
                return false;
            }
            return true;
        });
    }

    save() {
        if (this.validateCheckInQtyValues()) {
            this.savingMessage = [{ severity: Severity.Info, summary: 'Saving...' }];
            const newAssetEvents: AssetEvent[] = [];
            this.assets.forEach(asset => {
                const values = this.modalForm[asset.id].getRawValue();
                const newAssetEvent = new AssetEvent(this.assetEventsForAssetId[asset.id]);
                const checkInQty = values[this.checkInQtyField.canonical_name] ? Number(values[this.checkInQtyField.canonical_name]) : 0;
                newAssetEvent.qty_returned += checkInQty;
                newAssetEvent.qty_on_loan -= checkInQty;
                newAssetEvent.check_in_qty = checkInQty;
                newAssetEvents.push(newAssetEvent);
            });
            this.store.dispatch(new assetEventActions.UpdateMultipleAction({ event_id: this.eventId, assetEvents: newAssetEvents }));

            this.store.select(assetEventSelectors.getAssetEventSaving).pipe(
                filter(loading => loading !== ActionStatus.Loading),
                take(1))
                .subscribe((loading) => {
                    if (loading === ActionStatus.Complete) {
                        const message = `${pluralize('Asset', this.assets.length)} ${pluralize('has', this.assets.length)} been updated.`;
                        this.store.dispatch(new layoutActions.InfoAction({ message }));
                        this.store.dispatch(new assetActions.UpdateAction({ assets: this.assets }));
                        this.dialogRef.close();
                    } else {
                        this.savingMessage = [];
                    }
                });
        } else {
            this.savingMessage = [{ severity: Severity.Warn, summary: 'Check in quantity must be an integer greater than 0 and less than the quantity on loan' }];
        }
    }
}
